import type { Locale, SuggestionDetail } from "@divespots/contracts";
import { SUGGESTION_PAYLOAD_SCHEMAS } from "@divespots/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { TriangleAlert } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router";
import { api } from "../api/client.ts";
import { isApiRequestError } from "../api/errors.ts";
import { useSpots } from "../api/index.ts";
import { Button } from "../components/Button.tsx";
import { formatTimestamp } from "../i18n/format.ts";
import { useLocale } from "../i18n/index.ts";
import { diffRows } from "../suggest/diff.ts";
import {
  applyObjectPayload,
  applySpotPayload,
  objectCreatePayload,
  objectEditPayload,
  spotCreatePayload,
  spotEditPayload,
} from "../suggest/payloads.ts";
import { StatusChip, suggestionTitle } from "../suggest/ui.tsx";
import { ObjectFields } from "./ObjectFields.tsx";
import { SpotFields } from "./SpotFields.tsx";
import { emptyObjectForm, emptySpotForm, objectToForm, spotToForm } from "./forms.ts";
import type { FieldErrors, ObjectForm, SpotForm } from "./forms.ts";
import { Field, Section } from "./ui.tsx";

type Detail = SuggestionDetail;
type Body = Record<string, unknown>;

/** Payload vs current state, field by field. */
function Diff({ detail }: { detail: Detail }) {
  const { t } = useTranslation();
  const spots = useSpots();
  const s = detail.suggestion;
  const isEdit = s.kind.endsWith("_edit");
  // an object suggestion names its spot by id: show the name
  const shown: Body = { ...(s.payload as Body) };
  if (s.kind === "object_create" && typeof shown.spotId === "string") {
    const spot = spots.data?.find((x) => x.id === shown.spotId);
    if (spot) shown.spotId = spot.name;
  }
  const rows = diffRows(shown, isEdit ? detail.current : null);
  if (isEdit && detail.current === null) {
    return (
      <p className="adm-error m-0" role="alert">
        {t("review.targetGone")}
      </p>
    );
  }
  return (
    <table className="adm-diff" aria-label={t("review.diff")}>
      <thead>
        <tr>
          <th scope="col">{t("review.field")}</th>
          {isEdit ? <th scope="col">{t("review.before")}</th> : null}
          <th scope="col">{isEdit ? t("review.after") : t("review.value")}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.key} data-kind={r.kind}>
            <th scope="row">{t(`review.keys.${r.key}`, { defaultValue: r.key })}</th>
            {isEdit ? (
              <td className="adm-diff__before">
                {r.before.length ? (
                  r.before.map((l, i) => <div key={i}>{l}</div>)
                ) : (
                  <em>{t("review.empty_value")}</em>
                )}
              </td>
            ) : null}
            <td className="adm-diff__after">
              {r.after.length ? (
                r.after.map((l, i) => <div key={i}>{l}</div>)
              ) : (
                <em>{t("review.empty_value")}</em>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** The form that lets a moderator change the payload before approving it. */
function AdjustForm({
  detail,
  onPayload,
  errors,
}: {
  detail: Detail;
  onPayload: (p: Body) => void;
  errors: FieldErrors;
}) {
  const s = detail.suggestion;
  const [textLocale, setTextLocale] = useState<Locale>("de");
  const [formErrors, setErrors] = useState<FieldErrors>({});

  const spotBase = useMemo(
    () => (detail.current?.type === "spot" ? spotToForm(detail.current.spot) : emptySpotForm()),
    [detail.current],
  );
  const objBase = useMemo(
    () =>
      detail.current?.type === "object" ? objectToForm(detail.current.object) : emptyObjectForm(),
    [detail.current],
  );
  const [spot, setSpot] = useState<SpotForm>(() => applySpotPayload(spotBase, s.payload as Body));
  const [object, setObject] = useState<ObjectForm>(() =>
    applyObjectPayload(objBase, s.payload as Body),
  );
  const all = { ...errors, ...formErrors };

  if (s.kind === "spot_create" || s.kind === "spot_edit") {
    const set = <K extends keyof SpotForm>(k: K, v: SpotForm[K]) => {
      const next = { ...spot, [k]: v };
      setSpot(next);
      onPayload(
        s.kind === "spot_create" || detail.current?.type !== "spot"
          ? spotCreatePayload(next)
          : spotEditPayload(detail.current.spot, next),
      );
    };
    return (
      <SpotFields
        mode="diver"
        form={spot}
        errors={all}
        set={set}
        setForm={(u) => {
          const next = typeof u === "function" ? u(spot) : u;
          setSpot(next);
          onPayload(
            s.kind === "spot_create" || detail.current?.type !== "spot"
              ? spotCreatePayload(next)
              : spotEditPayload(detail.current.spot, next),
          );
        }}
        setErrors={setErrors}
        textLocale={textLocale}
        setTextLocale={setTextLocale}
      />
    );
  }
  const build = (next: ObjectForm): Body =>
    s.kind === "object_create"
      ? objectCreatePayload((s.payload as { spotId: string }).spotId, next)
      : detail.current?.type === "object"
        ? objectEditPayload(detail.current.object, next)
        : {};
  const set = <K extends keyof ObjectForm>(k: K, v: ObjectForm[K]) => {
    const next = { ...object, [k]: v };
    setObject(next);
    onPayload(build(next));
  };
  return (
    <ObjectFields
      mode="diver"
      form={object}
      errors={all}
      set={set}
      setForm={(u) => {
        const next = typeof u === "function" ? u(object) : u;
        setObject(next);
        onPayload(build(next));
      }}
      setErrors={setErrors}
      locale={textLocale}
      setLocale={setTextLocale}
      spotLocation={
        detail.current?.type === "object"
          ? { lat: detail.current.object.location.lat, lon: detail.current.object.location.lon }
          : {
              lat: object.lat ? Number(object.lat) : 46.8,
              lon: object.lon ? Number(object.lon) : 8.2,
            }
      }
    />
  );
}

/** `/admin/vorschlaege/:id`: review one suggestion. */
export function SuggestionDetailPage() {
  const { id = "" } = useParams();
  const { t } = useTranslation();
  const locale = useLocale();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const spots = useSpots();

  const q = useQuery({
    queryKey: ["admin", "suggestion", id],
    queryFn: ({ signal }) => api.getSuggestion(id, signal),
  });

  const [publish, setPublish] = useState(true);
  const [reason, setReason] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [adjust, setAdjust] = useState(false);
  const [adjusted, setAdjusted] = useState<Body | null>(null);
  const [force, setForce] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [banner, setBanner] = useState<string | null>(null);

  const done = async () => {
    await qc.invalidateQueries({ queryKey: ["admin"] });
    await qc.invalidateQueries({ queryKey: ["spots"] });
    await qc.invalidateQueries({ queryKey: ["spot"] });
    void navigate(`/${locale}/admin/vorschlaege`);
  };
  const fail = (e: unknown) => {
    if (isApiRequestError(e)) {
      if (e.code === "conflict") return setBanner(t("review.err.conflict"));
      if (e.code === "bad_request") return setBanner(t("review.err.badPayload"));
    }
    setBanner(t("admin.err.save"));
  };
  const approve = useMutation({
    mutationFn: (body: Parameters<typeof api.approveSuggestion>[1]) =>
      api.approveSuggestion(id, body),
    onSuccess: done,
    onError: fail,
  });
  const reject = useMutation({
    mutationFn: (r: string) => api.rejectSuggestion(id, { reason: r }),
    onSuccess: done,
    onError: fail,
  });

  if (q.isPending) return <p role="status">{t("common.loading")}</p>;
  if (q.isError) {
    return (
      <p className="adm-error" role="alert">
        {t("admin.err.load")}
      </p>
    );
  }
  const detail = q.data;
  const s = detail.suggestion;
  const pending = s.status === "pending";
  const isEdit = s.kind.endsWith("_edit");
  const slugFor = (spotId: string | null | undefined) =>
    spots.data?.find((x) => x.id === spotId)?.slug;
  const resultSlug = slugFor(
    s.kind === "spot_create" ? s.resultId : s.kind === "spot_edit" ? s.targetId : undefined,
  );

  const submitApprove = () => {
    setBanner(null);
    setErrors({});
    const body: Parameters<typeof api.approveSuggestion>[1] = {
      ...(isEdit ? {} : { publish }),
      ...(reason.trim() ? { reason: reason.trim() } : {}),
      ...(force ? { force: true } : {}),
    };
    if (adjust && adjusted) {
      const parsed = SUGGESTION_PAYLOAD_SCHEMAS[s.kind].safeParse(adjusted);
      if (!parsed.success) {
        setBanner(t("review.err.badPayload"));
        const out: FieldErrors = {};
        for (const i of parsed.error.issues)
          out[i.path.map(String).join(".")] = t("admin.err.invalid");
        return setErrors(out);
      }
      body.payload = adjusted;
    }
    approve.mutate(body);
  };
  const submitReject = () => {
    setBanner(null);
    if (!rejectReason.trim()) return setBanner(t("review.err.reasonRequired"));
    reject.mutate(rejectReason.trim());
  };

  return (
    <div className="grid gap-6">
      <div className="adm-pagehead">
        <h1 className="ds-text-title-1 m-0">{suggestionTitle(s, locale, t)}</h1>
        <Link className="ds-btn ds-btn--plain" to={`/${locale}/admin/vorschlaege`}>
          {t("review.back")}
        </Link>
      </div>
      <p className="ds-text-subheadline text-label-secondary m-0">
        {t(`suggest.kind.${s.kind}`)} · {s.submittedBy.name} ·{" "}
        {formatTimestamp(s.createdAt, locale)} <StatusChip status={s.status} />
      </p>
      {s.note ? (
        <Section title={t("suggest.note")}>
          <p className="ds-text-body m-0">{s.note}</p>
        </Section>
      ) : null}

      {detail.stale && pending ? (
        <p className="adm-warning" role="alert" data-testid="stale">
          <TriangleAlert aria-hidden="true" size={20} strokeWidth={1.75} />
          {t("review.stale")}
        </p>
      ) : null}

      <Section title={t("review.diff")}>
        <Diff detail={detail} />
      </Section>

      {!pending ? (
        <Section title={t("review.outcome")}>
          <p className="ds-text-body m-0">
            <StatusChip status={s.status} />
            {s.reviewedBy ? ` ${s.reviewedBy.name}` : ""}
            {s.reviewedAt ? ` · ${formatTimestamp(s.reviewedAt, locale)}` : ""}
          </p>
          {s.reviewReason ? <p className="ds-text-body m-0">{s.reviewReason}</p> : null}
          {s.adjusted ? <p className="ds-text-footnote m-0">{t("review.adjustedNote")}</p> : null}
          {resultSlug ? (
            <Link className="ds-btn ds-btn--tinted" to={`/${locale}/spot/${resultSlug}`}>
              {t("suggest.openSpot")}
            </Link>
          ) : null}
        </Section>
      ) : (
        <>
          <Section title={t("review.approveTitle")}>
            {!isEdit ? (
              <label className="adm-check">
                <input
                  type="checkbox"
                  checked={publish}
                  onChange={(e) => setPublish(e.target.checked)}
                />
                <span>{t("review.publish")}</span>
              </label>
            ) : null}
            <Field label={t("review.reason")} hint={t("review.reasonHint")}>
              {({ id, describedBy }) => (
                <textarea
                  id={id}
                  className="adm-input adm-textarea"
                  rows={2}
                  maxLength={500}
                  aria-describedby={describedBy}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              )}
            </Field>
            <label className="adm-check">
              <input
                type="checkbox"
                checked={adjust}
                onChange={(e) => {
                  setAdjust(e.target.checked);
                  // the adjusted payload belongs to the form that is mounted: a new form starts
                  // from the original again, so nothing typed into an earlier one may be sent
                  setAdjusted(null);
                  setErrors({});
                }}
              />
              <span>{t("review.adjust")}</span>
            </label>
            {adjust ? (
              <div className="adm-subform">
                <AdjustForm detail={detail} errors={errors} onPayload={setAdjusted} />
              </div>
            ) : null}
            {detail.stale ? (
              <label className="adm-check">
                <input
                  type="checkbox"
                  checked={force}
                  onChange={(e) => setForce(e.target.checked)}
                />
                <span>{t("review.force")}</span>
              </label>
            ) : null}
            <div className="adm-actions">
              <Button
                variant="filled-lake"
                disabled={approve.isPending || (detail.stale && !force)}
                onClick={submitApprove}
              >
                {t("review.approve")}
              </Button>
            </div>
          </Section>

          <Section title={t("review.rejectTitle")}>
            <Field label={`${t("review.reason")} *`} hint={t("review.rejectHint")}>
              {({ id, describedBy }) => (
                <textarea
                  id={id}
                  className="adm-input adm-textarea"
                  rows={2}
                  maxLength={500}
                  aria-describedby={describedBy}
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                />
              )}
            </Field>
            <div className="adm-actions">
              <Button variant="destructive" disabled={reject.isPending} onClick={submitReject}>
                {t("review.reject")}
              </Button>
            </div>
          </Section>
          {banner ? (
            <p className="adm-error m-0" role="alert">
              {banner}
            </p>
          ) : null}
        </>
      )}
    </div>
  );
}
